import { useState, useEffect } from 'react';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  IconDownload,
  ResponseError,
  SelectItemProps,
  ButtonSecondary,
  Select,
  Checkbox,
  DateInputRange,
} from '@ui/src';
import { useAppSelector } from '@store';
import getPeriodDateString from '../../helpers/get-period-string';
import { useLazyGetStatementItemsPdfQuery } from '../../../customers/store/services/customers';
import { StatementPanelVisibleTypes } from '../types';

import cn from '../styles.module.css';

const StatementPanel = ({
  statementPanelVisible,
  onCloseStatementPanel,
  selectedAccount,
  accounts,
  currencies,
}: StatementPanelVisibleTypes) => {
  const [selectedDateType, setSelectedDateType] = useState('today');
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<string>('csv');
  const [statementError, setStatementError] = useState('');
  const [currenciesList, setCurrenciesList] = useState<SelectItemProps[]>([]);

  const { currenciesList: dictionariesCurrencies } = useAppSelector(
    (state) => state.root,
  );

  const [getCustomersStatementPdf, { isLoading, isFetching, isSuccess }] =
    useLazyGetStatementItemsPdfQuery();

  const onDownloadStatement = async () => {
    const { dateString, dateFromValue, dateToValue } = getPeriodDateString(
      selectedDateType,
      selectedDateRange,
    );

    const datePeriod = `from ${new Date(
      dateFromValue as Date,
    ).toLocaleDateString()} to ${new Date(
      dateToValue as Date,
    ).toLocaleDateString()}`;

    const baseQuery = `accountId=${selectedAccount}&currencyId=${selectedCurrency}&${dateString}`;

    const currentAccountNumber = accounts?.find(
      (item) => item.id === selectedAccount,
    )?.fullNumber;

    const response = (await getCustomersStatementPdf({
      query: baseQuery,
      type: selectedFormat,
    })) as {
      error: ResponseError;
      data: any;
    };

    if (response.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `Statements ${
        currenciesList?.find((item) => item.value === selectedCurrency)?.name
      } #${currentAccountNumber} ${datePeriod}.${selectedFormat}`;
      hiddenElement.click();
    } else if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setStatementError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setStatementError(response.error.data.message);
        } else {
          setStatementError('Internal Server Error. Try Again');
        }
      }
    }
  };

  useEffect(() => {
    if (isSuccess) {
      onCloseStatementPanel();
      setStatementError('');
      setSelectedCurrency('');
      setSelectedDateType('today');
      setSelectedDateRange([]);
      setSelectedFormat('csv');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (currencies && dictionariesCurrencies) {
      const currenciesArray: SelectItemProps[] = [];

      dictionariesCurrencies?.forEach((item) => {
        if (currencies.find((currency) => currency === item.value)) {
          currenciesArray.push(item);
        }
      });

      setCurrenciesList(currenciesArray);
    }
  }, [currencies, dictionariesCurrencies]);

  return (
    <SidePanel
      title="Download account statement"
      isOpen={statementPanelVisible}
      onClose={() => {
        onCloseStatementPanel();
        setStatementError('');
        setSelectedCurrency('');
        setSelectedDateType('today');
        setSelectedDateRange([]);
        setSelectedFormat('csv');
      }}
      sidepanelExpandedKey="customers-download-statements"
      type={SidePanelType.AML}
      errorSidepanel={statementError}
      footer={
        <div className={cn.statementPanelControls}>
          <ButtonPrimary
            text={isLoading || isFetching ? 'Loading...' : 'Download'}
            icon={<IconDownload />}
            disabled={
              !selectedCurrency ||
              !selectedDateType ||
              !selectedFormat ||
              isLoading ||
              isFetching ||
              (selectedDateType === 'custom' && !selectedDateRange.length)
            }
            onClick={onDownloadStatement}
            bold
          />
          <ButtonSecondary
            className="button-white"
            text="Close"
            onClick={() => {
              onCloseStatementPanel();
              setStatementError('');
              setSelectedCurrency('');
              setSelectedDateType('today');
              setSelectedDateRange([]);
              setSelectedFormat('csv');
            }}
            bold
          />
        </div>
      }
      closeOnClickOutside
    >
      <>
        <div className={cn.currencyWrapper}>
          <Select
            name="selectedCurrecny"
            title="Currency"
            placeholder="Select currency"
            value={selectedCurrency}
            list={currenciesList || []}
            onChange={(e) => setSelectedCurrency(e.target.value as string)}
            withClearButton
            fullWidth
          />
        </div>
        <div className={cn.rangeTypeWrapper}>
          <div className={cn.rangeTypeTitle}>Select period</div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedDateType"
              type="radio"
              value="today"
              title="Today"
              checked={selectedDateType === 'today'}
              onChange={() => setSelectedDateType('today')}
            />
          </div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedDateType"
              type="radio"
              value="week"
              title="Week"
              checked={selectedDateType === 'week'}
              onChange={() => setSelectedDateType('week')}
            />
          </div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedDateType"
              type="radio"
              value="one-month"
              title="1 Month"
              checked={selectedDateType === 'one-month'}
              onChange={() => setSelectedDateType('one-month')}
            />
          </div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedDateType"
              type="radio"
              value="two-months"
              title="2 Months"
              checked={selectedDateType === 'two-months'}
              onChange={() => setSelectedDateType('two-months')}
            />
          </div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedDateType"
              type="radio"
              value="three-months"
              title="3 Months"
              checked={selectedDateType === 'three-months'}
              onChange={() => setSelectedDateType('three-months')}
            />
          </div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedDateType"
              type="radio"
              value="six-months"
              title="6 Months"
              checked={selectedDateType === 'six-months'}
              onChange={() => setSelectedDateType('six-months')}
            />
          </div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedDateType"
              type="radio"
              value="year"
              title="1 Year"
              checked={selectedDateType === 'year'}
              onChange={() => setSelectedDateType('year')}
            />
          </div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedDateType"
              type="radio"
              value="custom"
              title="Custom period"
              checked={selectedDateType === 'custom'}
              onChange={() => setSelectedDateType('custom')}
            />
          </div>
          {selectedDateType === 'custom' && (
            <div
              className={`${cn.dateInputRange} ${
                !selectedDateRange.length ? cn.empty : ''
              }`}
            >
              <div className={cn.dateInputTitle}>Account statement period</div>
              <DateInputRange
                values={selectedDateRange.map((item) => item.toISOString())}
                onChangeRange={(date) => setSelectedDateRange(date)}
                placeholder="Select date"
                withoutTwoPickers
                withoutCustomSelector
              />
            </div>
          )}
        </div>
        <div className={cn.rangeTypeWrapper}>
          <div className={cn.rangeTypeTitle}>Format</div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedFormat"
              type="radio"
              value="csv"
              title="CSV"
              checked={selectedFormat === 'csv'}
              onChange={() => setSelectedFormat('csv')}
            />
          </div>
          <div className={cn.radioWrapper}>
            <Checkbox
              name="selectedFormat"
              type="radio"
              value="pdf"
              title="PDF"
              checked={selectedFormat === 'pdf'}
              onChange={() => setSelectedFormat('pdf')}
            />
          </div>
        </div>
      </>
    </SidePanel>
  );
};

export default StatementPanel;
